import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Monogram } from "@/components/site/Logo";
import { Input } from "@/components/site/Form";
import { api, apiError } from "@/lib/api";

const Shell = ({ title, testid, children }) => (
  <main className="grid min-h-screen place-items-center bg-stone/40 px-5" data-testid={testid}>
    <div className="w-full max-w-sm border border-stone bg-paper p-8">
      <Monogram className="h-12 w-12" />
      <h1 className="mt-6 font-serif text-4xl">{title}</h1>
      {children}
      <Link to="/admin/login" className="mt-6 inline-block text-sm text-forest underline-offset-4 hover:underline" data-testid="back-to-login">Back to sign in</Link>
    </div>
  </main>
);

export function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ busy: false, done: "", err: "" });
  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, done: "", err: "" });
    try {
      const r = await api.post("/auth/forgot-password", { email });
      setState({ busy: false, done: r.data?.message ?? "Check your inbox.", err: "" });
    } catch (x) {
      setState({ busy: false, done: "", err: apiError(x) });
    }
  };
  return (
    <Shell title="Reset password" testid="admin-forgot-page">
      {state.done ? (
        <p className="mt-6 text-sm leading-relaxed" role="status" data-testid="forgot-done">{state.done} The link expires in 60 minutes.</p>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-5">
          <p className="text-sm text-charcoal/80">Enter your admin email and we'll send you a link to set a new password.</p>
          <Input id="forgot-email" label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
          {state.err && <p role="alert" className="text-sm text-[#8E2F25]" data-testid="forgot-error">{state.err}</p>}
          <button className="btn btn-primary w-full" disabled={state.busy} data-testid="forgot-submit">{state.busy ? "Sending…" : "Send reset link"}</button>
        </form>
      )}
    </Shell>
  );
}

export function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [f, setF] = useState({ pw: "", confirm: "" });
  const [state, setState] = useState({ busy: false, done: false, err: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (f.pw.length < 10) return setState({ ...state, err: "Password must be at least 10 characters" });
    if (f.pw !== f.confirm) return setState({ ...state, err: "Passwords do not match" });
    setState({ busy: true, done: false, err: "" });
    try {
      await api.post("/auth/reset-password", { token, new_password: f.pw });
      setState({ busy: false, done: true, err: "" });
    } catch (x) {
      setState({ busy: false, done: false, err: apiError(x) });
    }
  };
  return (
    <Shell title="Set a new password" testid="admin-reset-page">
      {state.done ? (
        <p className="mt-6 text-sm" role="status" data-testid="reset-done">Your password has been updated. You can now sign in with it.</p>
      ) : !token ? (
        <p className="mt-6 text-sm" data-testid="reset-missing-token">This link is incomplete. Please request a new reset link.</p>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-5">
          <Input id="reset-password" label="New password (at least 10 characters)" type="password" autoComplete="new-password" value={f.pw} onChange={(e) => setF({ ...f, pw: e.target.value })} required />
          <Input id="reset-confirm" label="Confirm new password" type="password" autoComplete="new-password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} required />
          {state.err && <p role="alert" className="text-sm text-[#8E2F25]" data-testid="reset-error">{state.err}</p>}
          <button className="btn btn-primary w-full" disabled={state.busy} data-testid="reset-submit">{state.busy ? "Saving…" : "Save new password"}</button>
        </form>
      )}
    </Shell>
  );
}
